import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress, Avatar } from '@mui/material';
import axios from 'axios';
import { API_BASE } from '../../utils/apiBase';

const displayValue = (v) => {
  if (v === undefined || v === null) return 'None';
  const s = String(v).trim();
  if (!s || s === '0') return 'None';
  return s;
};

const formatBirthdate = (v) => {
  if (v === undefined || v === null) return 'None';
  const s = String(v).trim();
  if (!s || s === '0') return 'None';
  if (s.includes('T')) return s.split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return s;
};

export default function MyProfileDialog({ open, onClose, user = null }) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const employeeid = user?.employeeid;

  const fallbackProfile = useMemo(() => {
    if (!user) return null;
    const fullName = [user.firstname, user.lastname].filter(Boolean).join(' ').trim() || user.username || null;
    return {
      employee_no: user.employeeid || user.employee_no || user.username || null,
      full_name: fullName,
      age: user.age ?? null,
      birthdate: user.birthdate || null,
      department: user.department || null,
      position: user.position || user.role || null,
      sss_number: user.sss_no || user.sss_number || null,
      pagibig_number: user.pagibig_no || user.pagibig_number || null,
      philhealth_number: user.philhealth_no || user.philhealth_number || null,
      tin_number: user.tin_no || user.tin_number || null,
    };
  }, [user]);

  useEffect(() => {
    if (!open) return;
    if (!employeeid) return;
    setLoading(true);
    setError('');
    setProfile(null);
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/me/profile`, { withCredentials: true });
        setProfile(res.data?.profile || fallbackProfile || null);
      } catch (e) {
        if (fallbackProfile) {
          setProfile(fallbackProfile);
          setError('');
        } else {
          setError('Failed to load profile.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [open, employeeid, fallbackProfile]);

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>My Profile</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && error && (
          <Typography color="error">{error}</Typography>
        )}
        {!loading && !error && (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Avatar
                sx={{ width: 88, height: 88, bgcolor: '#e9eef9', color: '#2f74d0', fontSize: 32 }}
              >
                {String(profile?.full_name || user?.firstname || user?.username || 'C').trim().charAt(0).toUpperCase()}
              </Avatar>
            </Box>
            <Typography><strong>Full Name:</strong> {displayValue(profile?.full_name)}</Typography>
            <Typography><strong>Employee No:</strong> {displayValue(profile?.employee_no)}</Typography>
            <Typography><strong>Age:</strong> {displayValue(profile?.age)}</Typography>
            <Typography><strong>Birthdate:</strong> {formatBirthdate(profile?.birthdate)}</Typography>
            <Typography><strong>Department:</strong> {displayValue(profile?.department)}</Typography>
            <Typography><strong>SSS Number:</strong> {displayValue(profile?.sss_number)}</Typography>
            <Typography><strong>Pag-IBIG Number:</strong> {displayValue(profile?.pagibig_number)}</Typography>
            <Typography><strong>PhilHealth Number:</strong> {displayValue(profile?.philhealth_number)}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
